import React, { useState, useEffect, useRef, useCallback } from "react";
import { Heart, ArrowLeft, ShoppingBag, Share2, ChevronLeft, ChevronRight, Sparkles, RefreshCw, Star, X, ExternalLink } from "lucide-react";
import Header from "../components/Header";
import MainFooter from "../components/MainFooter";
import API_URL from "../config/api";

// Same ProductCard component as HomeScreen to prevent flickering and maintain consistency
const ProductCard = React.memo(({ product, isInWishlist, onWishlistToggle, onProductClick }) => {
  return (
    <div 
      onClick={onProductClick}
      className="group relative bg-white rounded-xl sm:rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full cursor-pointer border border-slate-100 active:scale-95 sm:hover:-translate-y-1"
    >
      <div className="relative overflow-hidden aspect-square bg-slate-50 flex-shrink-0">
        {/* Static skeleton background - no animation */}
        <div className="absolute inset-0 bg-slate-100 product-skeleton" />
        
        <img 
          src={product.image_urls?.[0]}
          alt={product.title} 
          className="w-full h-full object-contain p-2 sm:p-4 transition-all duration-500 group-hover:scale-105 opacity-0"
          loading="lazy"
          onLoad={(e) => {
            e.target.style.opacity = '1';
            e.target.style.transition = 'opacity 0.3s ease-in';
            const skeleton = e.target.previousSibling;
            if (skeleton) skeleton.style.opacity = '0';
          }}
        />
        
        <button
          onClick={onWishlistToggle}
          type="button"
          className={`absolute top-2 right-2 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full shadow-md transition-all duration-200 active:scale-90 ${isInWishlist ? 'bg-red-50 text-red-500' : 'bg-white/90 backdrop-blur-sm text-slate-400'}`}
        >
          <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${isInWishlist ? 'fill-red-500' : ''}`} />
        </button>

        {product.savings_percentage && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md shadow-sm">
            -{product.savings_percentage}%
          </div>
        )}
      </div>

      <div className="p-3 sm:p-4 flex flex-col flex-grow">
        <span className="text-[10px] sm:text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1">
          {product.main_category || 'Gift'}
        </span>
        
        <h3 className="font-semibold text-slate-800 text-xs sm:text-sm line-clamp-2 mb-2 leading-snug">
          {product.title}
        </h3>
        
        <div className="mt-auto pt-2 border-t border-slate-50">
          <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
            <span className="text-sm sm:text-base font-bold text-[#06402B]">
              {product.price_display}
            </span>
            {product.list_price && product.list_price !== product.price && (
              <span className="text-[10px] sm:text-xs text-slate-400 line-through">
                ${Number(product.list_price).toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';

const ProductDetailPage = ({
  product,
  onClose,
  user,
  onLogout,
  allProducts = [],
  currentScreen,
  onNavigate,
  wishlist = [],
  toggleWishlist,
}) => {
  const [showFullFeatures, setShowFullFeatures] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [similarError, setSimilarError] = useState(null);
  const similarScrollRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setCurrentImageIndex(0);
    setShowFullFeatures(false);
    setShowFullDescription(false);
  }, [product?.asin]);

  const fetchSimilarProducts = useCallback(async () => {
    if (!product?.asin) return;
    setLoadingSimilar(true);
    setSimilarError(null);

    try {
      const response = await fetch(`${API_URL}/recommend/similar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wishlist_asins: [product.asin],
          exclude_asins: [product.asin],
          top_k: 8
        })
      });

      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setSimilarProducts(data.recommendations || []);
    } catch (error) {
      console.error('Error:', error);
      setSimilarError('Unable to load recommendations');
      const fallback = allProducts
        .filter(p => p.asin !== product.asin)
        .sort(() => Math.random() - 0.5)
        .slice(0, 8);
      setSimilarProducts(fallback);
    } finally {
      setLoadingSimilar(false);
    }
  }, [product?.asin, allProducts]);

  useEffect(() => {
    if (product?.asin) fetchSimilarProducts();
  }, [product?.asin, fetchSimilarProducts]);

  if (!product) return null;

  const images = product.image_urls || [product.image_urls?.[0]];
  const productUrl = product.affiliate_url || product.url || `https://www.amazon.com/dp/${product.asin}?tag=surpriseai01-20`;

  const handleShare = async () => {
    const shareData = {
      title: product.title,
      text: product.clean_description ? product.clean_description.slice(0, 100) + '...' : 'Check out this gift!',
      url: productUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      try {
        await navigator.clipboard.writeText(productUrl);
        alert('Product link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const scrollSimilar = (direction) => {
    if (similarScrollRef.current) {
      const scrollAmount = similarScrollRef.current.offsetWidth * 0.8;
      similarScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFCF6]">
      <Header user={user} onLogout={onLogout} currentScreen={currentScreen} onNavigate={onNavigate} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Back Button */}
        <button
          onClick={() => onClose()}
          type="button"
          className="flex items-center gap-2 text-slate-500 hover:text-[#06402B] transition-colors mb-4 text-sm font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to browsing
        </button>

        {/* Main Content - Medium Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-10">
          {/* Image Section */}
          <div className="space-y-3">
            <div 
              className="relative aspect-square rounded-2xl overflow-hidden bg-white shadow-md cursor-zoom-in border border-slate-100"
              onClick={() => setIsImageZoomed(true)}
            >
              <img
                src={images[currentImageIndex]}
                alt={product.title}
                className="w-full h-full object-contain p-4 sm:p-8"
              />
              
              {images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
                    }}
                    type="button"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white transition-all active:scale-95"
                  >
                    <ChevronLeft className="w-5 h-5 text-slate-700" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
                    }}
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-white transition-all active:scale-95"
                  >
                    <ChevronRight className="w-5 h-5 text-slate-700" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white text-xs font-medium">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide snap-x">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    type="button"
                    className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-all snap-start ${idx === currentImageIndex ? 'border-[#06402B] shadow-sm' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain bg-white p-1.5" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info Section - Medium */}
          <div className="flex flex-col">
            {(product.brand || product.main_category) && (
              <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
                {product.brand && <span>by <span className="font-medium text-slate-700">{product.brand}</span></span>}
                {product.brand && product.main_category && <span>•</span>}
                {product.main_category && (
                  <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
                    {product.main_category}
                  </span>
                )}
              </div>
            )}

            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800 mb-3 leading-snug">
              {product.title}
            </h1>

            {product.star_rating && (
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center bg-yellow-50 px-2 py-1 rounded-lg">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-3.5 h-3.5 ${i < Math.floor(product.star_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
                    />
                  ))}
                </div>
                <span className="text-sm font-medium text-slate-700">
                  {product.star_rating.toFixed(1)}
                </span>
                {product.review_count && (
                  <span className="text-xs text-slate-400">({product.review_count})</span>
                )}
              </div>
            )}

            {product.price_display && (
              <div className="mb-4">
                <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap">
                  <span className="text-2xl sm:text-3xl font-bold text-[#06402B]">
                    {product.price_display}
                  </span>
                  {product.list_price && product.list_price !== product.price && (
                    <>
                      <span className="text-base text-slate-400 line-through">
                        ${Number(product.list_price).toFixed(2)}
                      </span>
                      {product.savings_percentage && (
                        <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-bold rounded-lg">
                          Save {product.savings_percentage}%
                        </span>
                      )}
                    </>
                  )}
                </div>
                {product.availability && (
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                    {product.availability === 'IN_STOCK' ? 'In Stock' : product.availability}
                  </p>
                )}
              </div>
            )}

            {/* Medium Buttons */}
            <div className="space-y-2.5 mb-5">
              <div className="flex gap-2">
                <button
                  onClick={() => toggleWishlist(product.asin)}
                  type="button"
                  className={`flex-1 py-2.5 px-4 border-2 rounded-xl transition-all font-semibold flex items-center justify-center gap-2 text-sm active:scale-95 ${wishlist.includes(product.asin) ? 'border-red-200 bg-red-50 text-red-600' : 'border-[#06402B] text-[#06402B] hover:bg-[#06402B]/5'}`}
                >
                  <Heart className={`w-4 h-4 ${wishlist.includes(product.asin) ? "fill-current" : ""}`} />
                  {wishlist.includes(product.asin) ? 'Saved' : 'Add to Wishlist'}
                </button>

                <button
                  onClick={handleShare}
                  type="button"
                  className="px-4 py-2.5 border-2 border-slate-200 rounded-xl hover:border-[#06402B] hover:text-[#06402B] transition-colors active:scale-95"
                  title="Share product link"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </div>

              {product.asin && (
                <button
                  onClick={() => window.open(productUrl, "_blank", "noopener,noreferrer")}
                  type="button"
                  className="w-full py-3 bg-[#06402B] text-white rounded-xl hover:bg-[#053527] transition-all font-semibold flex items-center justify-center gap-2 text-sm shadow-md active:scale-95"
                >
                  <ShoppingBag className="w-4 h-4" />
                  Buy Now on Amazon
                  <ExternalLink className="w-3 h-3 opacity-70" />
                </button>
              )}
            </div>

            {/* Features - 3 Lines */}
            {product.features && product.features.length > 0 && (
              <div className="border-t border-slate-200 pt-4 mb-4">
                <h3 className="text-sm font-bold text-slate-800 mb-2.5 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-[#06402B]" />
                  Key Features
                </h3>
                <div className={`space-y-2 ${!showFullFeatures && product.features.length > 3 ? 'max-h-[72px] overflow-hidden relative' : ''}`}>
                  {(showFullFeatures ? product.features : product.features.slice(0, 3)).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-slate-600">
                      <span className="w-1.5 h-1.5 bg-[#06402B] rounded-full mt-1.5 flex-shrink-0"></span>
                      <span className="leading-relaxed">{feature}</span>
                    </div>
                  ))}
                  {!showFullFeatures && product.features.length > 3 && (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#FBFCF6] to-transparent" />
                  )}
                </div>
                {product.features.length > 3 && (
                  <button 
                    type="button"
                    onClick={() => setShowFullFeatures(!showFullFeatures)}
                    className="mt-2 text-[#06402B] font-semibold text-xs hover:underline"
                  >
                    {showFullFeatures ? 'Show Less' : `+${product.features.length - 3} more features`}
                  </button>
                )}
              </div>
            )}

            {/* Description - 3 Lines */}
            {product.clean_description && (
              <div className="border-t border-slate-200 pt-4">
                <h3 className="text-sm font-bold text-slate-800 mb-2.5 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-[#06402B]" />
                  Description
                </h3>
                <div className="relative">
                  <p className={`text-xs sm:text-sm text-slate-600 leading-relaxed ${!showFullDescription ? 'line-clamp-3' : ''}`}>
                    {product.clean_description}
                  </p>
                  {!showFullDescription && product.clean_description.length > 150 && (
                    <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-[#FBFCF6] to-transparent" />
                  )}
                </div>
                {product.clean_description.length > 150 && (
                  <button 
                    type="button"
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="mt-2 text-[#06402B] font-semibold text-xs hover:underline"
                  >
                    {showFullDescription ? 'Show Less' : 'Read More'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Similar Products - Using Same ProductCard as HomeScreen */}
        <section className="border-t border-slate-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-800 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-[#06402B]" />
                You May Also Like
              </h2>
            </div>
            
            <div className="flex items-center gap-1.5">
              <button
                onClick={fetchSimilarProducts}
                disabled={loadingSimilar}
                type="button"
                className="p-2 text-slate-500 hover:text-[#06402B] hover:bg-[#06402B]/5 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loadingSimilar ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => scrollSimilar('left')}
                type="button"
                className="hidden sm:block p-2 border border-slate-200 rounded-lg hover:border-[#06402B] hover:text-[#06402B] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollSimilar('right')}
                type="button"
                className="hidden sm:block p-2 border border-slate-200 rounded-lg hover:border-[#06402B] hover:text-[#06402B] transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {similarError && !loadingSimilar && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-700">
              {similarError}. Showing popular alternatives.
            </div>
          )}

          {loadingSimilar ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden border border-slate-100">
                  <div className="aspect-square bg-slate-100" />
                  <div className="p-3 space-y-2">
                    <div className="h-3 bg-slate-100 rounded w-2/3" />
                    <div className="h-3 bg-slate-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div 
              ref={similarScrollRef}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4"
            >
              {similarProducts.map(p => (
                <ProductCard 
                  key={p.asin} 
                  product={p}
                  isInWishlist={wishlist.includes(p.asin)}
                  onWishlistToggle={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleWishlist(p.asin);
                  }}
                  onProductClick={() => onClose(p)}
                />
              ))}
            </div>
          )}

          {!loadingSimilar && similarProducts.length === 0 && !similarError && (
            <div className="text-center py-8 bg-white rounded-xl border border-slate-100">
              <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No similar products found</p>
            </div>
          )}
        </section>
      </div>

      <MainFooter />

      {/* Zoom Modal */}
      {isImageZoomed && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setIsImageZoomed(false)}
        >
          <button
            onClick={() => setIsImageZoomed(false)}
            type="button"
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
                }}
                type="button"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
                }}
                type="button"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <img src={images[currentImageIndex]} alt={product.title} className="max-w-full max-h-[85vh] object-contain" />
        </div>
      )}

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .product-skeleton { transition: opacity 0.3s ease-out; }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ProductDetailPage;